import type { ParseKeys, TFunction } from 'i18next'

import { statusFromWire } from '@/features/cases/api/caseWire'
import { formatDecimalString, formatMoneyString } from '@/lib/format'
import { isSuppressed, type AuditEntry, type Maybe } from '@/types/domain'

/* The financial trail in the reader's words (small-bugs batch 3, H-1, owner
 * decision D3).
 *
 * An audit entry names its record by KIND and its change by machine FIELD, and
 * stores both values as the server wrote them: a raw enum code (`bank_transfer`),
 * an ISO date, a decimal amount with its currency, a uuid, and — for the
 * allocations and the tranche series — a short ENGLISH phrase composed by the
 * server ("233.33 CNY → tranche 2 of 3", "#2 233.33 CNY due 2026-11-02"). The
 * trail is append-only (0009's trigger), so the rows already written cannot be
 * re-worded where they are stored. They are worded HERE, at render, old rows
 * included.
 *
 * THE CONTRACT is backend/internal/finance/testdata/audit_phrases.golden.json,
 * which TestAuditVocabularyGolden generates by CALLING each composer and by
 * reading the writers' source. auditWording.test.ts reads it and fails when a
 * (kind, field) the server writes is not classified in AUDIT_FIELDS below, has
 * no label in either catalogue, or when a sample phrase does not parse into
 * words. A phrase is parsed with ANCHORED patterns, one per shape the composers
 * produce, and anything that matches none of them — a shape added server-side
 * ahead of this build — is printed VERBATIM: never a crash, never a blank.
 *
 * WHAT IS NEVER REWORDED: text a person typed. An invoice number, a payment
 * reference, an expense item, a vendor's name stay exactly as stored, and enter
 * the line by concatenation rather than through i18next's interpolator (which
 * would substitute a `{{slot}}` inside them — see notificationMessages.ts).
 *
 * WHAT IS NEVER PRINTED: an id. A field whose value is a uuid (`issuing_entity`,
 * `detail_of`, a debit note's `counterparty`) reads as «Задано» / «Изменено» /
 * «Снято» instead — a reader can act on "the entity changed", not on a uuid.
 *
 * Money is never computed: an amount is grouped digit for digit
 * (`formatDecimalString`), and the four-place FX settlement difference keeps
 * all four. */

/** How the values of one field read. */
export type AuditValueKind =
  /** `<decimal> <CCY>` — platform/money's canonical form. */
  | 'money'
  /** A bare decimal at full precision (AuditDecimal). */
  | 'decimal'
  /** An ISO `YYYY-MM-DD` business date. */
  | 'date'
  /** What a person typed: printed as stored. */
  | 'text'
  /** A uuid (or `<kind> <uuid>`): never printed. */
  | 'reference'
  /** `no tax` / `<rate>% VAT` (invoice_service.go vatWord). */
  | 'vat'
  /** `<amount> → <target>` (payment_service.go allocationAuditValue). */
  | 'allocation'
  /** `#N <amount> due <date>` / `#N <amount> no agreed date`. */
  | 'tranche'
  /** `#N due <date>` / `#N no agreed date`. */
  | 'trancheDate'
  | 'caseStatus'
  | 'paymentStatus'
  | 'documentStatus'
  | 'debitNoteStatus'
  | 'rateChangeStatus'
  | 'method'
  | 'direction'

/** Every field the trail carries, by record kind — mirrored from the golden
 *  file's `fields` plus its `retiredFields`, both directions held by the test.
 *
 *  By (kind, field) rather than field alone: `status` is written for a case, an
 *  invoice, a debit note, a payment and a rate-change request, each with its own
 *  set of values. */
export const AUDIT_FIELDS: Readonly<Record<string, Readonly<Record<string, AuditValueKind>>>> = {
  case: {
    'billed_total.migration_bypass': 'money',
    rate: 'money',
    receivableTranche: 'tranche',
    status: 'caseStatus',
    trancheExpectedDate: 'trancheDate',
  },
  debit_note: {
    amount: 'money',
    // "client <uuid>" / "participant <uuid>" (counterpartyWord).
    counterparty: 'reference',
    line_amount: 'money',
    note_date: 'date',
    number: 'text',
    status: 'debitNoteStatus',
  },
  expense_line: {
    amount: 'money',
    detail_of: 'reference',
    remarks: 'text',
    stands_alone: 'reference',
    // S-22d: the line's link to a directory counterparty — a uuid, so it reads
    // «Задано / Изменено / Снято»; the name beside it is `vendor_name`.
    vendor_id: 'reference',
    vendor_name: 'text',
  },
  invoice: {
    amount: 'money',
    invoice_date: 'date',
    invoice_number: 'text',
    line_amount: 'money',
    status: 'documentStatus',
    vat: 'vat',
  },
  payment: {
    'allocation.debit_note': 'allocation',
    // RETIRED (H-1, D4): "<amount> → <item> — <vendor>". No writer files it any
    // more; old rows reach a caller who may read the vendor, and arrive hidden
    // for everyone else.
    'allocation.expense': 'allocation',
    'allocation.expense_item': 'allocation',
    'allocation.invoice': 'allocation',
    'allocation.tranche': 'allocation',
    amount: 'money',
    // S-22d2: the payment's party by record — a uuid each, so they read
    // «Задано / Изменено / Снято»; the name beside them is `counterparty_name`.
    client_id: 'reference',
    counterparty_id: 'reference',
    counterparty_name: 'text',
    direction: 'direction',
    fee: 'money',
    fee_amount: 'money',
    fx_settlement_difference: 'decimal',
    issuing_entity: 'reference',
    issuing_entity_id: 'reference',
    method: 'method',
    received_on: 'date',
    reference: 'text',
    status: 'paymentStatus',
  },
  rate_change_request: {
    requested_rate: 'money',
    status: 'rateChangeStatus',
  },
}

/** Enum codes onto catalogue keys. Own-key lookups only, so `constructor` is
 *  not a payment method. */
const ENUMS: Readonly<Partial<Record<AuditValueKind, Readonly<Record<string, ParseKeys>>>>> = {
  method: {
    cash: 'finance.payments.methods.cash',
    bank_transfer: 'finance.payments.methods.bank_transfer',
  },
  direction: { in: 'finance.payments.in', out: 'finance.payments.out' },
  paymentStatus: {
    pending: 'finance.payments.statuses.pending',
    confirmed: 'finance.payments.statuses.confirmed',
  },
  // An invoice is «Выставлен»; the document status catalogue says exactly that.
  documentStatus: { draft: 'docStatus.draft', issued: 'docStatus.issued' },
  // A debit note is «Выставлена» — Russian agreement follows the noun.
  debitNoteStatus: {
    draft: 'cases.history.audit.values.debitNoteStatus.draft',
    issued: 'cases.history.audit.values.debitNoteStatus.issued',
  },
  rateChangeStatus: {
    pending: 'cases.history.audit.values.rateChangeStatus.pending',
    approved: 'cases.history.audit.values.rateChangeStatus.approved',
    rejected: 'cases.history.audit.values.rateChangeStatus.rejected',
  },
}

const AMOUNT = String.raw`(-?\d+(?:\.\d+)?) ([A-Z]{3})`
const ISO = String.raw`(\d{4}-\d{2}-\d{2})`

const MONEY = new RegExp(`^${AMOUNT}$`)
const DECIMAL = /^-?\d+(?:\.\d+)?$/
const ISO_DATE = new RegExp(`^${ISO}$`)
const VAT_RATE = /^(\d+(?:\.\d+)?)% VAT$/

/* The allocation's two halves, then its target by the shapes the target
   sources compose (tranches_target.go trancheLabel, invoice_target.go
   invoiceLabel, debitnote_target.go debitNoteLabel). */
const ALLOCATION = new RegExp(`^${AMOUNT} → (.+)$`, 'u')
const TRANCHE_OF = /^tranche (\d+) of (\d+)$/
const TRANCHE_N = /^tranche (\d+)$/
const INVOICE = /^invoice(?: (.+))?$/
const DEBIT_NOTE = /^debit note(?: (.+))?$/

/* A retired expense row's vendor half, when the line named none — the one
   Russian phrase the server ever stored (expense_target.go vendorUnstated). */
const VENDOR_UNSTATED = ' — поставщик не указан'

/* tranches_service.go trancheAuditValue / trancheDateAuditValue. */
const TRANCHE_DUE = new RegExp(`^#(\\d+) ${AMOUNT} due ${ISO}$`)
const TRANCHE_UNDATED = new RegExp(`^#(\\d+) ${AMOUNT} no agreed date$`)
const TRANCHE_DATE_DUE = new RegExp(`^#(\\d+) due ${ISO}$`)
const TRANCHE_DATE_UNDATED = /^#(\d+) no agreed date$/

/** What wording a value needs from its caller. */
export interface AuditWords {
  t: TFunction
  /** An ISO date in the reader's form — `useFormat().date`. */
  date: (iso: string) => string
}

/** One value in the reader's words. `worded` is false when the value matched
 *  no shape its field is known to carry, and `text` is then the value as
 *  stored. */
export interface WordedValue {
  text: string
  worded: boolean
}

function kindOf(recordKind: string, field: string): AuditValueKind | undefined {
  if (!Object.hasOwn(AUDIT_FIELDS, recordKind)) return undefined
  const fields = AUDIT_FIELDS[recordKind]!
  return Object.hasOwn(fields, field) ? fields[field] : undefined
}

/** A catalogue string, or the fallback when the key is missing or names a
 *  branch of the catalogue rather than a leaf (see CaseHistory's actionTitle
 *  for why `returnObjects` is asked for). */
function label(t: TFunction, key: string, fallback: string): string {
  const value = t(key as ParseKeys, { defaultValue: fallback, returnObjects: true })
  return typeof value === 'string' ? value : fallback
}

/** «Платёж · Способ оплаты». A kind or a field with no words is named by its
 *  machine name, which is ugly, present and searchable — not blank. */
export function auditTitle(recordKind: string, field: string, t: TFunction): string {
  const record = label(t, `cases.history.audit.records.${recordKind}`, recordKind)
  const name = label(t, `cases.history.audit.fields.${recordKind}.${field}`, field)
  return `${record} · ${name}`
}

const verbatim = (value: string): WordedValue => ({ text: value, worded: false })
const worded = (text: string): WordedValue => ({ text, worded: true })

function money(amount: string, currency: string): string {
  return formatMoneyString(amount, currency)
}

/** The target half of an allocation, or undefined for a shape it does not
 *  know. */
function allocationTarget(field: string, target: string, t: TFunction): string | undefined {
  switch (field) {
    case 'allocation.tranche': {
      const of = TRANCHE_OF.exec(target)
      if (of) return t('cases.money.trancheOfSeries', { n: of[1]!, total: of[2]! })
      const n = TRANCHE_N.exec(target)
      return n ? t('cases.money.trancheN', { n: n[1]! }) : undefined
    }
    case 'allocation.invoice': {
      const match = INVOICE.exec(target)
      if (!match) return undefined
      // The number is the document's own and is appended, never interpolated.
      const word = t('cases.history.audit.values.allocation.invoice')
      return match[1] ? `${word} ${match[1]}` : word
    }
    case 'allocation.debit_note': {
      const match = DEBIT_NOTE.exec(target)
      if (!match) return undefined
      const word = t('cases.history.audit.values.allocation.debitNote')
      return match[1] ? `${word} ${match[1]}` : word
    }
    case 'allocation.expense_item':
      // The item, as typed.
      return target
    case 'allocation.expense':
      // "<item> — <vendor>", both typed. Not split on the dash — either half
      // may hold one — so only the server's own unstated-vendor wording, at
      // the very end, is put into the reader's language.
      return target.endsWith(VENDOR_UNSTATED)
        ? `${target.slice(0, -VENDOR_UNSTATED.length)} — ${t(
            'cases.history.audit.values.allocation.vendorUnstated',
          )}`
        : target
    default:
      return undefined
  }
}

/** One stored value in the reader's words. */
export function wordAuditValue(
  recordKind: string,
  field: string,
  value: string,
  { t, date }: AuditWords,
): WordedValue {
  const kind = kindOf(recordKind, field)

  switch (kind) {
    case 'money': {
      const match = MONEY.exec(value)
      return match ? worded(money(match[1]!, match[2]!)) : verbatim(value)
    }
    case 'decimal':
      return DECIMAL.test(value) ? worded(formatDecimalString(value)) : verbatim(value)
    case 'date':
      return ISO_DATE.test(value) ? worded(date(value)) : verbatim(value)
    case 'text':
      return worded(value)
    case 'reference':
      return worded(t('cases.history.audit.values.reference.set'))
    case 'vat': {
      if (value === 'no tax') return worded(t('cases.history.audit.values.vat.none'))
      const match = VAT_RATE.exec(value)
      return match
        ? worded(t('cases.history.audit.values.vat.rate', { rate: formatDecimalString(match[1]!) }))
        : verbatim(value)
    }
    case 'allocation': {
      const match = ALLOCATION.exec(value)
      if (!match) return verbatim(value)
      const target = allocationTarget(field, match[3]!, t)
      return target === undefined
        ? verbatim(value)
        : worded(`${money(match[1]!, match[2]!)} → ${target}`)
    }
    case 'tranche': {
      const due = TRANCHE_DUE.exec(value)
      if (due) {
        return worded(
          t('cases.money.trancheExpected', {
            n: due[1]!,
            amount: money(due[2]!, due[3]!),
            date: date(due[4]!),
          }),
        )
      }
      const undated = TRANCHE_UNDATED.exec(value)
      return undated
        ? worded(
            t('cases.money.trancheUndated', { n: undated[1]!, amount: money(undated[2]!, undated[3]!) }),
          )
        : verbatim(value)
    }
    case 'trancheDate': {
      const due = TRANCHE_DATE_DUE.exec(value)
      if (due) {
        return worded(
          t('cases.history.audit.values.trancheDate.expected', { n: due[1]!, date: date(due[2]!) }),
        )
      }
      const undated = TRANCHE_DATE_UNDATED.exec(value)
      return undated
        ? worded(t('cases.history.audit.values.trancheDate.undated', { n: undated[1]! }))
        : verbatim(value)
    }
    case 'caseStatus': {
      const status = statusFromWire(value)
      return status ? worded(t(`caseStatus.${status}`)) : verbatim(value)
    }
    case undefined:
      return verbatim(value)
    default: {
      const codes = ENUMS[kind]
      return codes && Object.hasOwn(codes, value) ? worded(t(codes[value]!)) : verbatim(value)
    }
  }
}

/** What a history row shows for an entry's change. */
export type AuditTransition =
  /** old → new, each worded; a withheld pair is passed through as it came. */
  | { shape: 'pair'; oldValue: Maybe<string> | undefined; newValue: Maybe<string> }
  /** One word in place of a pair whose values may not be printed (an id). */
  | { shape: 'summary'; text: string }

function wordMaybe(
  entry: AuditEntry,
  value: Maybe<string> | undefined,
  words: AuditWords,
): Maybe<string> | undefined {
  if (value === undefined || value === '' || isSuppressed(value)) return value
  return wordAuditValue(entry.recordKind, entry.field, value, words).text
}

/* S-21: an allocation WITHDRAWN by a reversal (its value to nothing) or MOVED
   by an invoice issue's carry-over (a tranche value to an invoice value) reads
   as ONE sentence rather than a pair — the pair would print a half the reader
   cannot act on (an empty new value, or an English tranche phrase under an
   invoice field). Golden: `transitions` in audit_phrases.golden.json
   (allocationReversed, allocationCarried). Anything else is undefined, and the
   entry reads as an ordinary pair. */
function wordAllocationMove(entry: AuditEntry, words: AuditWords): string | undefined {
  if (kindOf(entry.recordKind, entry.field) !== 'allocation') return undefined
  const { oldValue, newValue } = entry
  if (typeof oldValue !== 'string' || oldValue === '') return undefined

  // A reversal: the server files nil as the new value (allocationReversed).
  if (!newValue) {
    const withdrawn = wordAuditValue(entry.recordKind, entry.field, oldValue, words)
    return `${words.t('cases.history.audit.values.allocation.reversed')}: ${withdrawn.text}`
  }

  // A carry-over: `allocation.invoice`, from "<amount> → tranche N[ of M]" to
  // "<amount> → invoice <number>". The number is appended, never interpolated.
  if (entry.field !== 'allocation.invoice' || typeof newValue !== 'string') return undefined
  const from = ALLOCATION.exec(oldValue)
  const to = ALLOCATION.exec(newValue)
  if (!from || !to) return undefined
  const of = TRANCHE_OF.exec(from[3]!)
  const n = of ? null : TRANCHE_N.exec(from[3]!)
  const invoice = INVOICE.exec(to[3]!)
  if ((!of && !n) || !invoice) return undefined

  const carried = of
    ? words.t('cases.history.audit.values.allocation.carriedOf', { n: of[1]!, total: of[2]! })
    : words.t('cases.history.audit.values.allocation.carried', { n: n![1]! })
  const target = invoice[1] ? `${carried} ${invoice[1]}` : carried
  return `${target} — ${money(to[1]!, to[2]!)}`
}

export function wordAuditTransition(entry: AuditEntry, words: AuditWords): AuditTransition {
  const { oldValue, newValue } = entry

  // A WITHHELD pair is shown exactly as before: SuppressedValue draws the
  // marker, whatever the field. Nothing about a hidden value is inferred.
  if (isSuppressed(oldValue) || isSuppressed(newValue)) {
    return { shape: 'pair', oldValue, newValue }
  }

  const moved = wordAllocationMove(entry, words)
  if (moved !== undefined) return { shape: 'summary', text: moved }

  if (kindOf(entry.recordKind, entry.field) === 'reference') {
    const had = oldValue !== undefined && oldValue !== ''
    const has = newValue !== ''
    if (had || has) {
      const key = had && has ? 'changed' : has ? 'set' : 'cleared'
      return { shape: 'summary', text: words.t(`cases.history.audit.values.reference.${key}`) }
    }
  }

  return {
    shape: 'pair',
    oldValue: wordMaybe(entry, oldValue, words),
    newValue: wordMaybe(entry, newValue, words) ?? '',
  }
}

/** The transition as plain text, for the search: what is rendered, and a
 *  withheld value not at all (CaseHistory's searchText says why). */
export function auditTransitionText(transition: AuditTransition): string[] {
  if (transition.shape === 'summary') return [transition.text]
  return [transition.oldValue, transition.newValue].filter(
    (value): value is string => value !== undefined && !isSuppressed(value),
  )
}

/** The rail icon family for an entry, by the record it sits on. */
export type AuditFamily = 'money' | 'invoice' | 'status' | 'record'

const RECORD_FAMILIES: Readonly<Record<string, AuditFamily>> = {
  payment: 'money',
  expense_line: 'money',
  rate_change_request: 'money',
  invoice: 'invoice',
  debit_note: 'invoice',
}

export function auditFamily(entry: Pick<AuditEntry, 'recordKind' | 'field'>): AuditFamily {
  if (entry.recordKind === 'case') return entry.field === 'status' ? 'status' : 'money'
  return Object.hasOwn(RECORD_FAMILIES, entry.recordKind)
    ? RECORD_FAMILIES[entry.recordKind]!
    : 'record'
}
